import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackScreenView,
  trackFeatureUsed,
  trackCTAClick,
  trackContentView,
  trackCustomEvent,
  getCurrentPageLocation,
  getScreenNameFromPath,
} from '../services/analyticsService';
import { useAuth } from '../components/auth/AuthContext';
import { useIsSubscribed } from './useIsSubscribed';
import { useSubscription } from './useSubscription';
import { STRIPE_CONFIG } from '../config/stripe';

/**
 * React hook for Firebase Analytics
 * 
 * Provides easy access to analytics functions and automatically tracks screen views
 * 
 * @returns {Object} Analytics functions and helpers
 * 
 * @example
 * const analytics = useAnalytics();
 * 
 * // Track feature usage
 * analytics.trackFeatureUsed({
 *   section: 'editor',
 *   feature: 'ai_suggest',
 *   action: 'invoke'
 * });
 * 
 * // Track CTA click
 * analytics.trackCTAClick({
 *   section: 'pricing',
 *   action: 'start_trial'
 * });
 */
export const useAnalytics = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { isSubscribed } = useIsSubscribed();
  const { subscription } = useSubscription();

  // Auto-track screen views on route changes
  useEffect(() => {
    const pageLocation = getCurrentPageLocation();
    const screenName = getScreenNameFromPath(location.pathname);

    trackScreenView({
      page_location: pageLocation,
      firebase_screen: screenName,
      section: getSectionFromPath(location.pathname),
    });
  }, [location.pathname]);

  /**
   * Get section from pathname
   * @param {string} pathname - Current pathname
   * @returns {string} Section name
   */
  const getSectionFromPath = (pathname) => {
    if (pathname.startsWith('/creditplan')) return 'credit_plan';
    if (pathname.startsWith('/actionable')) return 'actionable_plans';
    if (pathname.startsWith('/history')) return 'assessment_history';
    if (pathname.startsWith('/manage-subscription')) return 'billing';
    if (pathname.startsWith('/business-credit')) return 'business_credit';
    if (pathname.startsWith('/personal-credit')) return 'personal_credit';
    return 'dashboard';
  };

  /**
   * Get user plan tier from subscription data
   * @returns {string|null} Plan tier ('free', 'monthly', 'yearly') or null
   */
  const getUserPlanTier = () => {
    if (!isSubscribed || !subscription) {
      return 'free';
    }

    // Check subscription priceId to determine plan tier
    if (subscription.priceId) {
      if (STRIPE_CONFIG.isMonthlyProduct(subscription.priceId)) {
        return 'monthly';
      }
      if (STRIPE_CONFIG.isYearlyProduct(subscription.priceId)) {
        return 'yearly';
      }
    }

    // Fallback: if subscribed but can't determine tier, return 'premium'
    return 'premium';
  };

  return {
    // Core tracking functions
    trackScreenView: (params) => {
      trackScreenView({
        ...params,
        plan_tier: getUserPlanTier(),
      });
    },
    trackFeatureUsed: (params) => {
      trackFeatureUsed({
        ...params,
        plan_tier: params.plan_tier || getUserPlanTier(),
      });
    },
    trackCTAClick: (params) => {
      trackCTAClick({
        ...params,
        plan_tier: params.plan_tier || getUserPlanTier(),
      });
    },
    trackContentView: (params) => {
      trackContentView({
        ...params,
        plan_tier: params.plan_tier || getUserPlanTier(),
      });
    },
    trackCustomEvent,
    
    // Helpers
    getCurrentPageLocation,
    getScreenNameFromPath,
    getCurrentSection: () => getSectionFromPath(location.pathname),
  };
};

export default useAnalytics;

